import React, { useState, useEffect } from 'react';
import axiosClient from "../AxiosClient";
import { useParams, Link } from 'react-router-dom';
import { Footer, Header } from '../Components';
import FAQSection from '../Components/FAQSection';
import FooterTopBar from "../Components/FooterTopBar";
import ReviewComponent from '../Components/ReviewComponent';
import DoctorInfoComponent from '../Components/DoctorInfoComponent';
import SimilarDoctors from '../Components/SimilarDoctors';
import { getSpecializationNames } from '../Services/SpecializationService';

const AvailabilityBadge = ({ available }) => (
  <div className={`flex items-center text-xs font-medium rounded-full px-2 py-0.5 w-fit
    ${available ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
    <span className={`w-3 h-3 mr-1 rounded-full ${available ? 'bg-green-500' : 'bg-red-500'}`}></span>
    {available ? "Available" : "Not Available"}
  </div>
);

const DoctorPage = () => {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {

    axiosClient.get(`/doctor_view/${id}`)
      .then((response) => {
        if (response) {
          setDoctor(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching doctor data:", error);
      })
      .finally(() => {
        setLoading(false);
      });

  }, [id]);

  console.log('doctor: ', doctor); // Log the doctor data to debug

  if (loading) return <div className="text-center mt-8 sm:mt-10 px-4">Loading...</div>;
  if (!doctor) return <div className="text-center mt-8 sm:mt-10 text-red-500 px-4">Doctor not found.</div>;

  const specializationNames = getSpecializationNames(doctor);
  const specializationName = specializationNames[0] || 'Medical Professional';

  // Format matricule (medical degrees) as a comma-separated string
  const formatMatricule = () => {
    if (!doctor?.matricule) return '';
    let values = doctor.matricule;
    if (typeof doctor.matricule === 'string') {
      const trimmed = doctor.matricule.trim();
      if (trimmed === '') return '';
      try {
        const parsed = JSON.parse(trimmed);
        values = Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        values = trimmed.split(',');
      }
    }
    if (!Array.isArray(values)) return '';
    return values
      .map((v) => (typeof v === 'string' ? v.trim() : v))
      .filter((v) => v !== '' && v != null)
      .join(', ');
  };

  return (
    <>
      <Header />
      <h1 className="text-left text-xl sm:text-2xl mt-8 sm:mt-12 font-bold px-4 max-w-7xl mx-auto">
        Connect with Best {specializationName}
      </h1>

      <div className="w-full max-w-7xl mx-auto my-10 px-4 md:px-6">
        {/* Doctor Profile Card */}
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
          {/* Header Section */}
          <div className="h-32 sm:h-40" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}></div>
          
          {/* Content Section */}
          <div className="px-4 sm:px-6 md:px-8 pb-6 -mt-16 sm:-mt-20">
            <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8 space-y-6 lg:space-y-0">
              
              {/* Doctor Image & Basic Info */}
              <div className="flex flex-col items-center lg:items-start space-y-4 lg:w-1/3">
                <div className="relative">
                  <img 
                    src={doctor?.avatar_doctor || "/img/doc-listing.jpg"} 
                    alt="Doctor profile" 
                    className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl object-cover border-4 border-white shadow-lg bg-gray-100"
                  />
                  <div className="absolute -bottom-2 -right-2">
                    <AvailabilityBadge available={doctor?.available} />
                  </div>
                </div>
                
                <div className="text-center lg:text-left w-full">
                  {/* Doctor Name with PMDC Verified Badge */}
                  <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-2">
                    <h2 className="font-bold text-2xl sm:text-3xl text-gray-900">
                      Dr. {doctor?.firstname} {doctor?.lastname}
                    </h2>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 shadow-sm">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      PMDC Verified
                    </span>
                  </div>

                  {/* Medical Degrees Badge */}
                  {formatMatricule() && (
                    <div className="mb-2 flex justify-center lg:justify-start">
                      <span className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-semibold bg-blue-50  border border-blue-200" style={{ color: '#3d75ba' }}>
                        <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
                        </svg>
                        {formatMatricule()}
                      </span>
                    </div>
                  )}

                  {/* Specialization */}
                  <p className="font-semibold text-lg mt-1 mb-3" style={{ color: '#3d75ba' }}>{specializationName}</p>
                  
                  {/* Experience and Consultation Duration */}
                  {doctor?.experience_years && (
                    <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 mb-3">
                      <div className="flex items-center text-gray-700 text-sm">
                        <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="font-medium">{doctor.experience_years} years experience</span>
                      </div>
                      <span className="text-gray-400">•</span>
                      <div className="flex items-center text-gray-700 text-sm">
                        <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">15-30 minutes</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Location Info */}
                  <div className="mt-3 space-y-2 text-sm text-gray-600">
                    {doctor?.city?.name && (
                      <div className="flex items-center justify-center lg:justify-start">
                        <i className="fas fa-map-marker-alt mr-2 " style={{ color: '#3d75ba' }}></i>
                        <span>{doctor.city.name}, {doctor?.state?.name}</span>
                      </div>
                    )}
                    {doctor?.address_cabinet && (
                      <div className="flex items-start justify-center lg:justify-start">
                        <i className="fas fa-clinic-medical mr-2 mt-1" style={{ color: '#3d75ba' }}></i>
                        <span className="text-left">{doctor.address_cabinet}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Specializations & Description */}
              <div className="flex-1 lg:w-1/3 space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 text-lg">Specializations</h3>
                  <div className="flex flex-wrap gap-2">
                    {specializationNames.length > 0 ? (
                      specializationNames.map((name) => (
                        <span
                          key={name}
                          className="bg-blue-100  px-3 py-1.5 rounded-full text-sm font-medium"
                          style={{ color: '#3d75ba' }}
                        >
                          {name}
                        </span>
                      ))
                    ) : (
                      <span className="bg-blue-100  px-3 py-1.5 rounded-full text-sm font-medium" style={{ color: '#3d75ba' }}>
                        {specializationName}
                      </span>
                    )}
                  </div>
                  
                  {doctor?.primary_specialization?.description && (
                    <div className="mt-4">
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {doctor.primary_specialization.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Consultation Cards */}
              <div className="lg:w-1/3 space-y-4">
                {/* Video Consultation Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-lg border border-blue-200">
                  <div className="flex items-center mb-3">
                    <i className="fas fa-video text-xl mr-2" style={{color:'#587FD9'}}></i>
                    <h3 className="font-semibold text-blue-900">Video Consultation</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Consultation Fee</span>
                      <span className="text-blue-900 font-bold text-lg">
                        ${doctor?.video_fee || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Status</span>
                      <AvailabilityBadge available={doctor?.available} />
                    </div>
                  </div>
                  <Link to={`/bookingappointment/${id}`} state={{ feeType: 'video' }} className="block mt-4">
                    <button className="w-full  text-white py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                      Book Video Consultation
                    </button>
                  </Link>
                </div>

                {/* Clinic Consultation Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-lg border border-green-200">
                  <div className="flex items-center mb-3">
                    <i className="fas fa-clinic-medical text-green-600 text-xl mr-2"></i>
                    <h3 className="font-semibold text-green-900">Clinic Visit</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Consultation Fee</span>
                      <span className="text-green-900 font-bold text-lg">
                        ${doctor?.clinic_fee || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 text-sm">Status</span>
                      <AvailabilityBadge available={doctor?.available} />
                    </div>
                  </div>
                  <Link to={`/bookingappointment/${id}`} state={{ feeType: 'clinic' }} className="block mt-4">
                    <button className="w-full bg-green-600 text-white py-2.5 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-md">
                      Book Clinic Appointment
                    </button>
                  </Link>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <ReviewComponent doctorId={id} />
      <DoctorInfoComponent doctorId={id} />
      <FAQSection />
      <SimilarDoctors />
      <FooterTopBar />
      <Footer />
    </>
  );
};

export default DoctorPage;
